import React, { useEffect, useRef, useMemo } from "react";
import { Modal, Button } from "react-bootstrap";
import { useI18n } from "../i18n";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { Tracker } from "../pages/Dashboard";

interface PriceChartModalProps {
  show: boolean;
  onHide: () => void;
  tracker: Tracker;
}

const PriceChartModal: React.FC<PriceChartModalProps> = ({
  show,
  onHide,
  tracker,
}) => {
  const { t } = useI18n();
  const chartDivRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<am5xy.XYChart | null>(null);
  const rootRef = useRef<am5.Root | null>(null);
  const xAxisRef = useRef<am5xy.DateAxis<am5xy.AxisRenderer> | null>(null);
  const initialZoomRef = useRef<{ start: number; end: number } | null>(null);
  const scrollPosRef = useRef<number>(0);
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  // Preserve scroll position when modal opens/closes and reset fullscreen
  useEffect(() => {
    if (show) {
      scrollPosRef.current = window.pageYOffset || document.documentElement.scrollTop;
    } else {
      setIsFullscreen(false); // Reset fullscreen when modal closes
      setTimeout(() => {
        window.scrollTo(0, scrollPosRef.current);
      }, 0);
    }
  }, [show]);

  const sortedHistory = useMemo(() => {
    const history = [...tracker.price_history].sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    );

    // Ensure the chart reflects the current total price
    const latestTotal =
      tracker.flight_details?.total_price ?? tracker.current_price ?? null;
    if (latestTotal !== null) {
      const lastPoint = history[history.length - 1];
      if (!lastPoint || lastPoint.price !== latestTotal) {
        history.push({
          timestamp: new Date().toISOString(),
          price: latestTotal,
        });
      }
    }

    return history;
  }, [
    tracker.price_history,
    tracker.flight_details?.total_price,
    tracker.current_price,
  ]);

  useEffect(() => {
    if (!show || !chartDivRef.current || sortedHistory.length === 0) return;

    // Prevent double root creation in React Strict Mode
    if (rootRef.current) {
      return;
    }

    const chartDiv = chartDivRef.current; // Copy ref for cleanup

    // Create root
    const root = am5.Root.new(chartDiv);
    rootRef.current = root;

    // Set themes
    root.setThemes([am5themes_Animated.new(root)]);

    // Create chart
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: false,
        wheelX: "panX",
        wheelY: "zoomX",
        pinchZoomX: true,
        paddingLeft: 0,
        paddingRight: 20,
        layout: root.verticalLayout, // Stack legend and chart vertically
      })
    );

    // Add legend at top (before chart to avoid scrollbar overlap)
    const legend = chart.children.unshift(
      am5.Legend.new(root, {
        centerX: am5.p50,
        x: am5.p50,
        marginBottom: 15,
      })
    );

    // Add cursor with zoom behavior
    const cursor = chart.set("cursor", am5xy.XYCursor.new(root, {
      behavior: "zoomX",
    }));
    cursor.lineY.set("visible", false);

    // Create axes
    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        baseInterval: {
          timeUnit: "hour",
          count: 1,
        },
        maxDeviation: 0.5,
        groupData: false,
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 80,
        }),
        tooltip: am5.Tooltip.new(root, {}),
      })
    );

    // Store xAxis reference for reset zoom
    xAxisRef.current = xAxis;

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
        extraMin: 0.1, // Add 10% padding below minimum value
        extraMax: 0.15, // Add 15% padding above maximum value for label visibility
      })
    );

    // Format Y axis labels as currency
    yAxis.get("renderer").labels.template.adapters.add("text", (text) => {
      const value = parseFloat(text || "0");
      if (!isNaN(value)) {
        return "€" + value.toFixed(2);
      }
      return text;
    });

    // Prepare data
    const chartData = sortedHistory.map((item) => ({
      date: new Date(item.timestamp).getTime(),
      price: item.price,
    }));

    // Create main price series
    const series = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: t("chart.priceLabel"),
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "price",
        valueXField: "date",
        tooltip: am5.Tooltip.new(root, {
          labelText: "{valueY}",
        }),
        stroke: am5.color(0x0d6efd),
        fill: am5.color(0x0d6efd),
      })
    );

    // Configure series appearance with smooth curve
    series.strokes.template.setAll({
      strokeWidth: 3,
    });

    series.fills.template.setAll({
      fillOpacity: 0.15,
      visible: true,
    });

    // Add bullets (points) with data labels
    series.bullets.push(() => {
      const bulletCircle = am5.Circle.new(root, {
        radius: 5,
        fill: series.get("fill"),
        stroke: root.interfaceColors.get("background"),
        strokeWidth: 2,
      });

      return am5.Bullet.new(root, {
        sprite: bulletCircle,
      });
    });

    // Add data labels (price values on points)
    series.bullets.push((root, series, dataItem) => {
      const label = am5.Label.new(root, {
        text: "",
        fill: am5.color(0x000000),
        centerY: am5.p100,
        centerX: am5.p50,
        dy: -10,
        populateText: true,
        oversizedBehavior: "none",
        textAlign: "center",
        fontSize: 12,
        fontWeight: "600",
      });

      // Format text as currency
      label.adapters.add("text", (text, target) => {
        const value = dataItem.get("valueY") as number;
        if (typeof value === "number") {
          return "€" + value.toFixed(2);
        }
        return text;
      });

      // Hide label on mobile or when price hasn't changed from previous point
      label.adapters.add("forceHidden", () => {
        // Hide on mobile
        if (root.width() < 768) {
          return true;
        }
        
        // Check if price changed from previous data point
        const currentPrice = dataItem.get("valueY") as number;
        const dataIndex = series.data.indexOf(dataItem.dataContext as any);
        
        if (dataIndex > 0) {
          const previousData = series.data.getIndex(dataIndex - 1);
          const previousPrice = (previousData as any)?.price;
          
          // Hide if price is the same as previous
          if (previousPrice !== undefined && currentPrice === previousPrice) {
            return true;
          }
        }
        
        return false;
      });

      return am5.Bullet.new(root, {
        sprite: label,
      });
    });

    // Format tooltip
    series.get("tooltip")!.label.adapters.add("text", (text, target) => {
      const dataContext = target.dataItem?.dataContext as any;
      if (dataContext) {
        const date = new Date(dataContext.date);
        const dateStr = new Intl.DateTimeFormat("it-IT", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }).format(date);
        const priceStr = new Intl.NumberFormat("it-IT", {
          style: "currency",
          currency: "EUR",
        }).format(dataContext.price);
        return `${dateStr}\n${t("chart.priceLabel")}: ${priceStr}`;
      }
      return text;
    });

    series.data.setAll(chartData);

    // Add threshold line if set
    if (
      tracker.threshold_price !== null &&
      tracker.threshold_price !== undefined
    ) {
      const thresholdSeries = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: t("chart.thresholdLabel"),
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: "threshold",
          valueXField: "date",
          stroke: am5.color(0xdc3545),
        })
      );

      thresholdSeries.strokes.template.setAll({
        strokeWidth: 2,
        strokeDasharray: [6, 6],
      });

      // Hide bullets for threshold line
      thresholdSeries.bullets.clear();

      const thresholdData = chartData.map((item) => ({
        date: item.date,
        threshold: tracker.threshold_price,
      }));

      thresholdSeries.data.setAll(thresholdData);

      // Add red threshold indicator on Y-axis
      const axisRange = yAxis.createAxisRange(
        yAxis.makeDataItem({
          value: tracker.threshold_price,
        })
      );

      axisRange.get("grid")?.setAll({
        stroke: am5.color(0xdc3545),
        strokeWidth: 2,
        strokeOpacity: 0.8,
        strokeDasharray: [6, 6],
      });

      axisRange.get("label")?.setAll({
        fill: am5.color(0xdc3545),
        text: "€" + tracker.threshold_price.toFixed(2),
        fontSize: 11,
        fontWeight: "600",
        background: am5.RoundedRectangle.new(root, {
          fill: am5.color(0xffffff),
          fillOpacity: 0.9,
        }),
        paddingLeft: 5,
        paddingRight: 5,
        paddingTop: 2,
        paddingBottom: 2,
      });
    }

    // Add scrollbar with zoom preview
    chart.set(
      "scrollbarX",
      am5.Scrollbar.new(root, {
        orientation: "horizontal",
      })
    );

    // Add series to legend
    legend.data.setAll(chart.series.values);

    // Calculate last 24 hours zoom range
    const now = Date.now();
    const twentyFourHoursAgo = now - (24 * 60 * 60 * 1000);
    
    // Get the full data range
    const minDataDate = Math.min(...chartData.map(d => d.date));
    const maxDataDate = Math.max(...chartData.map(d => d.date));
    const totalRange = maxDataDate - minDataDate;
    
    // Find data points within last 24 hours
    const recentData = chartData.filter(d => d.date >= twentyFourHoursAgo);
    
    if (recentData.length > 0 && totalRange > 0) {
      // If we have data in last 24 hours, zoom to that range
      const minDate = Math.min(...recentData.map(d => d.date));
      const maxDate = Math.max(...recentData.map(d => d.date));
      
      // Calculate positions as fractions of the total data range
      const startPos = (minDate - minDataDate) / totalRange;
      const endPos = (maxDate - minDataDate) / totalRange;
      
      // Store initial zoom for reset button (with small padding)
      const padding = 0.02; // 2% padding
      initialZoomRef.current = { 
        start: Math.max(0, startPos - padding), 
        end: Math.min(1, endPos + padding) 
      };
      
      // Apply initial zoom after chart appears
      series.events.once("datavalidated", () => {
        setTimeout(() => {
          xAxis.zoom(
            initialZoomRef.current!.start,
            initialZoomRef.current!.end,
            500
          );
        }, 150);
      });
    } else {
      // If no data in last 24h or all data points have same timestamp, show all data
      initialZoomRef.current = { start: 0, end: 1 };
    }

    // Animate on load
    series.appear(1000);
    chart.appear(1000, 100);

    chartRef.current = chart;

    // Cleanup
    return () => {
      chartDiv.innerHTML = "";
      root.dispose();
      rootRef.current = null;
      chartRef.current = null;
      xAxisRef.current = null;
    };
  }, [show, sortedHistory, tracker.threshold_price, t]);

  const handleResetZoom = () => {
    if (xAxisRef.current && initialZoomRef.current) {
      // Reset to initial 24-hour view with animation
      xAxisRef.current.zoom(
        initialZoomRef.current.start,
        initialZoomRef.current.end,
        300
      );
    } else if (xAxisRef.current) {
      // If no initial zoom stored, show all data
      xAxisRef.current.zoom(0, 1, 300);
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="xl"
      dialogClassName={isFullscreen ? "modal-fullscreen" : "modal-chart-large"}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {t("chart.titlePrefix")} {tracker.origin} → {tracker.destination}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <small className="text-muted">
            <i className="bi bi-info-circle me-1"></i>
            {t("chart.zoomInstructions")}
          </small>
          <div className="d-flex gap-2">
            <Button 
              size="sm" 
              variant="outline-secondary" 
              onClick={() => setIsFullscreen(!isFullscreen)}
              title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
              className="d-none d-md-inline-flex"
            >
              <i className={`bi bi-${isFullscreen ? 'fullscreen-exit' : 'fullscreen'} me-1`}></i>
              {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={handleResetZoom}>
              <i className="bi bi-arrow-counterclockwise me-1"></i>
              {t("chart.resetZoom")}
            </Button>
          </div>
        </div>
        <div
          ref={chartDivRef}
          style={{
            width: "100%",
            height: isFullscreen ? "calc(100vh - 180px)" : "500px",
          }}
        />
      </Modal.Body>
    </Modal>
  );
};

export default React.memo(PriceChartModal);
